import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { setComplaints } from "../Redux/complaints/action";
import { setErrorStatus } from "../Redux/status/action";
import { getComplaint } from "../Services/database";
import { database } from "../Services/firebase";
import { isValidObject } from "../Services/validators";
import { complaintsQueryBuilder } from "../Utils/Complaints";

const dataLimit = 50;

export function useComplaints(props) {
  const complaintsListener = useRef(null);
  const complaintsData = useRef(null);
  const currentCursor = useRef(null);
  const filterQuery = useRef([]);
  const listeningFilterData = useRef([]);
  const [searchData, setSearchData] = useState(null);
  const [complaintsLoading, setComplaintsLoading] = useState({
    pagination: false,
    search: false
  });

  const subscribeToComplaints = (sortBy) => {
    if (!props.phoneNumber) {
      return;
    }

    let queryRef = query(
      collection(database, "complaints"),
      where("connectedPhoneNumbers", "array-contains", props.phoneNumber),
      where("status.currentStatus", "==", "OPEN"),
      orderBy("createdAt", sortBy || "desc"),
      limit(dataLimit)
    );
    filterQuery.current.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        queryRef = query(
          queryRef,
          where(
            filterQuery.current[index - 2],
            filterQuery.current[index - 1],
            value
          )
        );
      }
    });

    return onSnapshot(
      queryRef,
      (dataSnapshot) => {
        let complaints = {};
        dataSnapshot.forEach((doc) => {
          complaints[doc.id] = {
            ...doc.data(),
            documentId: doc.id
          };
        });

        currentCursor.current = dataSnapshot.docs[dataSnapshot.docs.length - 1];

        if (
          complaintsData.current &&
          Object.values(complaintsData.current)?.length > dataLimit
        ) {
          const previousComplaintsTotal =
            Object.values(complaintsData.current)?.length - dataLimit;
          getComplaintsNextPage(previousComplaintsTotal, complaints);
        } else if (searchData) {
          if (isValidObject(searchData)) {
            complaintsData.current = null;
            complaintsData.current = {
              [props.searchKey]: {
                ...searchData,
                documentId: props.searchKey
              }
            };
          } else {
            complaintsData.current = null;
            complaintsData.current = {};
          }
        } else {
          complaintsData.current = complaints;
        }
        setComplaints(
          complaintsData.current,
          filterQuery.current,
          props.navigate
        );
      },
      (error) => {
        console.error(error, "from complaints");
        setErrorStatus(error, "useComplaints");
      }
    );
  };

  useEffect(() => {
    if (props.searchKey?.length === 8) {
      getSearchedComplaint(props.searchKey);
    } else if (
      (props.searchKey?.length < 8 || props.searchKey?.length > 8) &&
      searchData !== null
    ) {
      setSearchData(null);
    }
    // eslint-disable-next-line
  }, [props.searchKey]);

  useEffect(() => {
    if (props.isAuth && typeof complaintsListener.current === "function") {
      complaintsListener.current = subscribeToComplaints(props.sortBy);
    }
    // eslint-disable-next-line
  }, [props.sortBy, searchData]);

  useEffect(() => {
    const query = complaintsQueryBuilder(props.filterData, props.locationData);
    filterQuery.current = query;

    if (
      typeof complaintsListener.current === "function" &&
      props.isAuth &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      complaintsData.current = null;
      listeningFilterData.current = filterQuery.current;
      complaintsListener.current();
      complaintsListener.current = subscribeToComplaints(props.sortBy);
    }
    // eslint-disable-next-line
  }, [props.filterData, props.locationData]);

  useEffect(() => {
    if (
      props.isAuth &&
      props.phoneNumber &&
      complaintsListener.current === null
    ) {
      complaintsListener.current = subscribeToComplaints(props.sortBy);
    } else if (
      !props.isAuth &&
      typeof complaintsListener.current === "function"
    ) {
      complaintsListener.current();
      complaintsListener.current = null;
    }

    return () => {
      if (typeof complaintsListener.current === "function") {
        complaintsListener.current();
        complaintsListener.current = null;
      }
    };
    // eslint-disable-next-line
  }, [props.isAuth, props.phoneNumber]);

  const getComplaintsNextPage = async (preLoadedLimit, latestComplaints) => {
    if (!currentCursor?.current) {
      return;
    }

    setComplaintsLoading((prev) => ({ ...prev, pagination: true }));

    let paginationQueryRef = query(
      collection(database, "complaints"),
      where("connectedPhoneNumbers", "array-contains", props.phoneNumber),
      where("status.currentStatus", "==", "OPEN"),
      orderBy("createdAt", props.sortBy || "desc"),
      startAfter(currentCursor?.current),
      limit(preLoadedLimit !== undefined ? preLoadedLimit : dataLimit)
    );

    listeningFilterData.current?.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        paginationQueryRef = query(
          paginationQueryRef,
          where(
            listeningFilterData.current[index - 2],
            listeningFilterData.current[index - 1],
            value
          )
        );
      }
    });

    const querySnapshot = await getDocs(paginationQueryRef);
    let complaints = {};
    querySnapshot.forEach((doc) => {
      complaints[doc.id] = {
        ...doc.data(),
        documentId: doc.id
      };
    });

    if (latestComplaints) {
      complaintsData.current = { ...latestComplaints, ...complaints };
    } else {
      complaintsData.current = { ...complaintsData.current, ...complaints };
    }

    currentCursor.current = querySnapshot.docs[querySnapshot.docs.length - 1];
    setComplaintsLoading((prev) => ({ ...prev, pagination: false }));

    setComplaints(complaintsData.current, filterQuery.current, props.navigate);
  };
  // need to verify about search only opened complaints for employee
  const getSearchedComplaint = async (complaintId) => {
    setComplaintsLoading((prev) => ({ ...prev, search: true }));
    const existData = complaintsData.current?.[complaintId.toUpperCase()];
    if (isValidObject(existData)) {
      setSearchData(existData);
    } else {
      const searchComplaint = await getComplaint(complaintId);
      if (isValidObject(searchComplaint)) {
        setSearchData(searchComplaint);
      } else {
        setSearchData("empty");
      }
    }
    setComplaintsLoading((prev) => ({ ...prev, search: false }));
  };

  return [getComplaintsNextPage, complaintsLoading];
}
